import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  TextField,
  DialogContentText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { alpha } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import dayjs from "dayjs";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import Overlay from "components/Overlay/Overlay";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";

const ContactPersonMergeOverlay = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const {
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    closeDialog,
  } = useStateContext();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const contactPersons = useMemo(() => {
    return Array.isArray(overlayData?.ContactPersons) ? overlayData.ContactPersons : [];
  }, [overlayData?.ContactPersons]);

  const contactPersonById = useMemo(() => {
    return new Map(contactPersons.map((cp) => [Number(cp.id), cp]));
  }, [contactPersons]);

  const selectedId = Number(overlayData?.SelectedContactPerson?.id);

  const normalizeName = useCallback((value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const damerauLevenshteinDistance = useCallback((a, b) => {
    const left = String(a || "");
    const right = String(b || "");

    const leftLength = left.length;
    const rightLength = right.length;

    if (leftLength === 0) return rightLength;
    if (rightLength === 0) return leftLength;

    const matrix = Array.from({ length: leftLength + 1 }, () =>
      new Array(rightLength + 1).fill(0),
    );

    for (let i = 0; i <= leftLength; i += 1) {
      matrix[i][0] = i;
    }

    for (let j = 0; j <= rightLength; j += 1) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= leftLength; i += 1) {
      for (let j = 1; j <= rightLength; j += 1) {
        const substitutionCost = left[i - 1] === right[j - 1] ? 0 : 1;

        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + substitutionCost,
        );

        if (
          i > 1 &&
          j > 1 &&
          left[i - 1] === right[j - 2] &&
          left[i - 2] === right[j - 1]
        ) {
          matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + 1);
        }
      }
    }

    return matrix[leftLength][rightLength];
  }, []);

  const normalizedSimilarity = useCallback((left, right) => {
    const a = String(left || "");
    const b = String(right || "");
    const maxLength = Math.max(a.length, b.length);

    if (!maxLength) return 1;

    const distance = damerauLevenshteinDistance(a, b);
    return Math.max(0, 1 - distance / maxLength);
  }, [damerauLevenshteinDistance]);

  const getNameMatchPercent = useCallback((baseName, candidateName) => {
    const base = normalizeName(baseName);
    const candidate = normalizeName(candidateName);

    if (!base || !candidate) return 0;
    if (base === candidate) return 100;

    const compactBase = base.replace(/\s+/g, "");
    const compactCandidate = candidate.replace(/\s+/g, "");
    const fullNameScore = normalizedSimilarity(compactBase, compactCandidate);

    const baseTokens = base.split(" ").filter(Boolean);
    const candidateTokens = candidate.split(" ").filter(Boolean);

    const tokenLengthsTotal = baseTokens.reduce(
      (sum, token) => sum + token.length,
      0,
    );

    const tokenScore =
      tokenLengthsTotal > 0
        ? baseTokens.reduce((sum, token) => {
            const bestTokenSimilarity = candidateTokens.reduce(
              (best, otherToken) =>
                Math.max(best, normalizedSimilarity(token, otherToken)),
              0,
            );
            return sum + bestTokenSimilarity * token.length;
          }, 0) / tokenLengthsTotal
        : 0;

    const firstNameScore = normalizedSimilarity(
      baseTokens[0] || "",
      candidateTokens[0] || "",
    );
    const lastNameScore = normalizedSimilarity(
      baseTokens[baseTokens.length - 1] || "",
      candidateTokens[candidateTokens.length - 1] || "",
    );

    const finalScore =
      fullNameScore * 0.55 +
      tokenScore * 0.2 +
      firstNameScore * 0.1 +
      lastNameScore * 0.15;

    return Math.max(0, Math.min(100, Math.round(finalScore * 100)));
  }, [normalizeName, normalizedSimilarity]);

  const initialMergeIds = useMemo(() => {
    if (!selectedId || !contactPersonById.has(selectedId)) {
      return contactPersons.length ? [Number(contactPersons[0].id)] : [];
    }
    return [selectedId];
  }, [contactPersonById, contactPersons, selectedId]);

  const initialOtherIds = useMemo(() => {
    const mergeSet = new Set(initialMergeIds);
    const keepName = contactPersonById.get(Number(initialMergeIds[0]))?.Name || "";

    return contactPersons
      .map((cp) => Number(cp.id))
      .filter((id) => !mergeSet.has(id))
      .map((id, index) => ({
        id,
        index,
        score: getNameMatchPercent(keepName, contactPersonById.get(id)?.Name),
      }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.index - b.index;
      })
      .map((item) => item.id);
  }, [getNameMatchPercent, initialMergeIds, contactPersonById, contactPersons]);

  const [contactPersonsToMergeIds, setContactPersonsToMergeIds] = useState(initialMergeIds);
  const [otherContactPersonIds, setOtherContactPersonIds] = useState(initialOtherIds);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [otherSearch, setOtherSearch] = useState("");

  const filteredOtherContactPersonIds = useMemo(() => {
    const query = String(otherSearch || "").trim().toLowerCase();
    if (!query) return otherContactPersonIds;

    return otherContactPersonIds.filter((cpId) => {
      const cp = contactPersonById.get(Number(cpId));
      if (!cp) return false;

      const name = String(cp.Name || "").toLowerCase();
      return name.includes(query);
    });
  }, [otherSearch, otherContactPersonIds, contactPersonById]);

  const keptContactPersonName = useMemo(() => {
    const keepId = Number(contactPersonsToMergeIds[0]);
    return contactPersonById.get(keepId)?.Name || "";
  }, [contactPersonById, contactPersonsToMergeIds]);

  // Lightweight client-side preview of what will move to the kept contact
  // person (counts from the already-loaded list rows — no server round-trip).
  const mergePreview = useMemo(() => {
    if (contactPersonsToMergeIds.length < 2) return null;
    const keepId = Number(contactPersonsToMergeIds[0]);
    const keep = contactPersonById.get(keepId);
    const mergeContactPersons = contactPersonsToMergeIds
      .slice(1)
      .map((id) => contactPersonById.get(Number(id)))
      .filter(Boolean);

    let phones = 0;
    let emails = 0;
    mergeContactPersons.forEach((cp) => {
      phones +=
        (cp.PhoneNumbers?.length || 0) + (cp.PhoneNumber ? 1 : 0);
      if (cp.Email) emails += 1;
    });

    return {
      keepName: keep?.Name || "the first contact person",
      mergeCount: mergeContactPersons.length,
      phones,
      emails,
    };
  }, [contactPersonsToMergeIds, contactPersonById]);

  const formatDate = (value) => {
    if (!value) return "N/A";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("M/D/YYYY") : "N/A";
  };

  const moveContactPersonToMerge = (cpId, targetIndex) => {
    const cpIdNumber = Number(cpId);
    if (!otherContactPersonIds.includes(cpIdNumber)) return;

    const nextOther = otherContactPersonIds.filter((id) => id !== cpIdNumber);
    const safeIndex =
      typeof targetIndex === "number"
        ? Math.max(0, Math.min(targetIndex, contactPersonsToMergeIds.length))
        : contactPersonsToMergeIds.length;

    const nextMerge = [...contactPersonsToMergeIds];
    nextMerge.splice(safeIndex, 0, cpIdNumber);

    setContactPersonsToMergeIds(nextMerge);
    setOtherContactPersonIds(nextOther);
  };

  const sortOtherContactPersonIdsByMatch = useCallback(
    (ids, mergeIds) => {
      const keepId = Number((mergeIds || [])[0]);
      const keepName = contactPersonById.get(keepId)?.Name || "";

      return [...ids]
        .map((id, index) => ({
          id,
          index,
          score: getNameMatchPercent(keepName, contactPersonById.get(Number(id))?.Name),
        }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.index - b.index;
        })
        .map((item) => item.id);
    },
    [getNameMatchPercent, contactPersonById],
  );

  const moveContactPersonToOther = (cpId, targetIndex) => {
    const cpIdNumber = Number(cpId);
    if (!contactPersonsToMergeIds.includes(cpIdNumber)) return;

    const nextMerge = contactPersonsToMergeIds.filter((id) => id !== cpIdNumber);
    const nextOther = [...otherContactPersonIds];
    if (!nextOther.includes(cpIdNumber)) {
      nextOther.push(cpIdNumber);
    }

    const sortedOther = sortOtherContactPersonIdsByMatch(nextOther, nextMerge);

    setContactPersonsToMergeIds(nextMerge);
    setOtherContactPersonIds(sortedOther);
  };

  const handleDragEnd = (result) => {
    setIsDragging(false);

    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const sourceList = source.droppableId === "merge-contact-persons" ? contactPersonsToMergeIds : otherContactPersonIds;
    const destList = destination.droppableId === "merge-contact-persons" ? contactPersonsToMergeIds : otherContactPersonIds;
    const [movedId] = sourceList.splice(source.index, 1);

    if (source.droppableId === destination.droppableId) {
      const newList = [...sourceList];
      newList.splice(destination.index, 0, movedId);
      if (source.droppableId === "merge-contact-persons") {
        setContactPersonsToMergeIds(newList);
      } else {
        setOtherContactPersonIds(newList);
      }
    } else {
      const newSourceList = sourceList.filter((_, i) => i !== source.index);
      const newDestList = [...destList];
      newDestList.splice(destination.index, 0, movedId);

      if (destination.droppableId === "merge-contact-persons") {
        setContactPersonsToMergeIds(newDestList);
        const sortedOther = sortOtherContactPersonIdsByMatch(newSourceList, newDestList);
        setOtherContactPersonIds(sortedOther);
      } else {
        setOtherContactPersonIds(newDestList);
        setContactPersonsToMergeIds(newSourceList);
      }
    }
  };

  const mergeContactPersons = async () => {
    if (contactPersonsToMergeIds.length < 2) {
      showSnackbar(
        "Select at least 2 contact persons in 'Contact Persons to Merge'. The first one is kept.",
        theme,
        "error",
      );
      return;
    }

    const keepContactPersonId = Number(contactPersonsToMergeIds[0]);
    const mergeContactPersonIds = contactPersonsToMergeIds.slice(1).map(Number);

    try {
      setIsSaving(true);

      const response = await api.post("/contactPersons/merge", {
        data: {
          clientId: Number(overlayData?.ClientID),
          keepContactPersonId,
          mergeContactPersonIds,
        },
      });

      const movedLinksCount =
        Number(response?.data?.movedLinksCount) || 0;

      if (socket && overlayData?.ClientID) {
        socket.emit("entity:saved", {
          modelType: "Client",
          recordId: Number(overlayData.ClientID),
          changeType: "ContactPerson",
          changeAction: "Merged",
          user: {
            id: currentUser?.id,
            name: currentUser?.Name || "User",
            imageThumbnail: currentUser?.ImageThumbnail,
          },
        });
      }

      triggerPageRefresh(
        `Contact persons merged successfully. Re-linked ${movedLinksCount} service link(s).`,
        theme,
      );

      closeDialog();
      setOverlayName("");
    } catch (error) {
      const statusCode = error?.response?.status;
      const baseMessage =
        error?.response?.data?.error ||
        "An error occurred while merging contact persons.";
      const message = statusCode
        ? `${baseMessage} (HTTP ${statusCode})`
        : baseMessage;
      closeDialog();
      showSnackbar(message, theme, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMergeClick = () => {
    if (contactPersonsToMergeIds.length < 2) {
      showSnackbar(
        "Select at least 2 contact persons in 'Contact Persons to Merge'. The first one is kept.",
        theme,
        "error",
      );
      return;
    }

    const mergedCount = contactPersonsToMergeIds.length - 1;
    const keepName = keptContactPersonName || "the first contact person";
    const preview = mergePreview;

    const movedParts = [];
    if (preview) {
      if (preview.phones > 0)
        movedParts.push(`${preview.phones} phone number(s)`);
      if (preview.emails > 0) movedParts.push(`${preview.emails} email(s)`);
    }

    openDialog({
      theme,
      title: "Merge Contact Persons?",
      message: `Merge ${mergedCount} contact person${mergedCount === 1 ? "" : "s"} into "${keepName}"? This cannot be undone.`,
      customContent: (
        <Box>
          <DialogContentText>
            {`Merge ${mergedCount} contact person${mergedCount === 1 ? "" : "s"} into "${keepName}"? This cannot be undone.`}
          </DialogContentText>
          {preview && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: "12px",
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.primary.border2}`,
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Merge preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {movedParts.length > 0
                  ? `Will move to "${preview.keepName}": ${movedParts.join(", ")}. `
                  : "No phones or emails found on the merged contact person(s). "}
                Services and history are re-linked; duplicate phones are
                skipped.
              </Typography>
            </Box>
          )}
        </Box>
      ),
      primaryAction: "Merge",
      secondaryAction: "Cancel",
      primaryOnClick: mergeContactPersons,
      secondaryOnClick: closeDialog,
    });
  };

  const renderContactPersonCard = (cpId, index, listType) => {
    const cp = contactPersonById.get(Number(cpId));
    if (!cp) return null;

    const isMergeList = listType === "merge";
    const isPrimaryKept = isMergeList && index === 0;

    const backgroundColor = isPrimaryKept
      ? alpha(theme.palette.primary.main, 0.12)
      : isMergeList
      ? alpha(theme.palette.warning.main, 0.12)
      : theme.palette.background.default;

    const borderColor = isPrimaryKept
      ? theme.palette.primary.main
      : isMergeList
      ? theme.palette.warning.main
      : theme.palette.primary.border2;

    const chipLabel = isPrimaryKept ? "Keep" : isMergeList ? "Merge" : "Other";
    const nameMatchPercent = isMergeList
      ? null
      : getNameMatchPercent(keptContactPersonName, cp.Name);

    return (
      <Box
        key={`contact-person-${cp.id}`}
        sx={{
          mb: 1,
          p: 1.25,
          borderRadius: "12px",
          bgcolor: backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.secondary,
                cursor: "grab",
              }}
            >
              <DragIndicatorRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="body1" fontWeight={700}>
              {cp.Name || "Unnamed Contact Person"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                bgcolor: isPrimaryKept
                  ? theme.palette.primary.main
                  : isMergeList
                  ? theme.palette.warning.main
                  : theme.palette.background.alt,
                color:
                  isPrimaryKept || isMergeList
                    ? theme.palette.background.default
                    : theme.palette.text.primary,
                fontWeight: 700,
              }}
            />
            {isMergeList ? (
              <IconButton
                size="small"
                onClick={() => moveContactPersonToOther(cp.id)}
                aria-label="Remove from merge list"
              >
                <RemoveRoundedIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => moveContactPersonToMerge(cp.id)}
                aria-label="Add to merge list"
              >
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {(() => {
          const phones = cp.PhoneNumbers || [];
          const primary = phones.find((p) => p.IsPrimary) || phones[0];
          const displayPhone = primary
            ? `${primary.Number ? normalizePhoneNumber(primary.CountryCode ? `+${primary.CountryCode} ${primary.Number}` : primary.Number) : ""}${primary.Ext ? ` (Ext: ${primary.Ext})` : ""}`
            : cp.PhoneNumber
              ? `${cp.PhoneNumber ? normalizePhoneNumber(cp.PhoneNumber) : ""}${cp.Ext ? ` (Ext: ${cp.Ext})` : ""}`
              : null;
          return displayPhone ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Phone: {displayPhone}
            </Typography>
          ) : null;
        })()}
        {cp.Email && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Email: {cp.Email}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          Added: {formatDate(cp.createdAt)}
        </Typography>
        {!isMergeList && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Name Match: {nameMatchPercent}%
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Overlay
      id="contact-person-merge"
      open={true}
      onClose={() => setOverlayName("")}
      title="Merge Contact Persons"
      subtitle="Drag contact persons between lists or use +/- buttons. In the top list, the first contact person is kept and the rest are merged into it."
      maxWidthPx={1600}
      height="90vh"
      contentBg="default"
      scrollable={false}
      footer={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "flex-end" }}>
          <Button
            color="primary"
            variant="outlined"
            size="large"
            endIcon={<DisabledByDefaultRoundedIcon />}
            onClick={() => setOverlayName("")}
            sx={{ borderRadius: "12px" }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            endIcon={<MergeRoundedIcon />}
            disabled={isSaving || contactPersonsToMergeIds.length === 0}
            onClick={handleMergeClick}
            sx={{ borderRadius: "12px" }}
          >
            Merge
          </Button>
        </Box>
      }
    >
        <Box
          sx={{
            display: "flex",
            p: "1rem 1.5rem",
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DragDropContext
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
          >
            <Grid
              container
              spacing={2}
              sx={{ width: "100%", minHeight: 0, height: "100%" }}
            >
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: { xs: "auto", md: "100%" },
                    minHeight: 0,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.default,
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    Contact Persons to Merge
                  </Typography>
                <Droppable droppableId="merge-contact-persons" type="CONTACT_PERSON">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        height: { xs: "30vh", md: "100%" },
                        maxHeight: { xs: "30vh", md: "100%" },
                        overflowY: "auto",
                        borderRadius: "12px",
                        p: 0.5,
                        bgcolor:
                          snapshot.isDraggingOver || isDragging
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                      }}
                    >
                      {contactPersonsToMergeIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          Add at least two contact persons here to merge.
                        </Typography>
                      ) : (
                        contactPersonsToMergeIds.map((cpId, index) => (
                          <Draggable key={cpId} draggableId={String(cpId)} index={index}>
                            {(drag) => (
                              <Box
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                              >
                                {renderContactPersonCard(cpId, index, "merge")}
                              </Box>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
                </Box>
              </Grid>

              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: { xs: "auto", md: "100%" },
                    minHeight: 0,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.default,
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    Other Contact Persons
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search other contact persons"
                    value={otherSearch}
                    onChange={(event) => setOtherSearch(event.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Droppable droppableId="other-contact-persons" type="CONTACT_PERSON">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          height: { xs: "30vh", md: "100%" },
                          maxHeight: { xs: "30vh", md: "100%" },
                          overflowY: "auto",
                          borderRadius: "12px",
                          p: 0.5,
                          bgcolor:
                            snapshot.isDraggingOver || isDragging
                              ? alpha(theme.palette.warning.main, 0.1)
                              : "transparent",
                        }}
                      >
                        {otherContactPersonIds.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            No contact persons remaining.
                          </Typography>
                        ) : filteredOtherContactPersonIds.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            No contact persons match your search.
                          </Typography>
                        ) : (
                          filteredOtherContactPersonIds.map((cpId, index) => (
                            <Draggable key={cpId} draggableId={String(cpId)} index={index}>
                              {(drag) => (
                                <Box
                                  ref={drag.innerRef}
                                  {...drag.draggableProps}
                                  {...drag.dragHandleProps}
                                >
                                  {renderContactPersonCard(cpId, index, "other")}
                                </Box>
                              )}
                            </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              </Grid>
            </Grid>
          </DragDropContext>
        </Box>
    </Overlay>
  );
};

export default ContactPersonMergeOverlay;
